import { Helmet } from "react-helmet-async";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SUPPORTED_LANGUAGES,
  getDomainForLang,
  buildCanonicalUrl,
  getSeoConfigForPath,
  type SupportedLanguage,
} from "@/lib/seoConfig";

interface PageSeoProps {
  // Optional overrides
  title?: string;
  description?: string;
  noindex?: boolean;
  path?: string; // Override path detection
}

export const PageSeo = ({ title, description, noindex, path: pathOverride }: PageSeoProps) => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();

  // Determine current language
  const currentLang = (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage))
    ? lang
    : 'en';

  // Extract path without language prefix
  let currentPath = pathOverride ?? location.pathname;
  // Remove language prefix
  const langPattern = new RegExp(`^/(${SUPPORTED_LANGUAGES.join('|')})/?`);
  currentPath = currentPath.replace(langPattern, '');
  currentPath = currentPath.replace(/^\/+/, '').replace(/\/+$/, '');

  // Get SEO config for this path
  const seoConfig = getSeoConfigForPath(currentPath);

  // Determine final values
  const finalTitle = title ?? (seoConfig ? t(seoConfig.titleKey, { defaultValue: 'eStyling Studio' }) : 'eStyling Studio');
  const finalDescription = description ?? (seoConfig ? t(seoConfig.descriptionKey, { defaultValue: '' }) : '');
  const finalNoindex = noindex ?? seoConfig?.noindex ?? false;

  // Build canonical URL for current page
  const canonicalUrl = buildCanonicalUrl(currentLang, currentPath);

  // Build hreflang URLs for all languages
  const hreflangLinks = SUPPORTED_LANGUAGES.map((langCode) => ({
    lang: langCode,
    url: buildCanonicalUrl(langCode, currentPath),
  }));

  // x-default points to English version
  const xDefaultUrl = buildCanonicalUrl('en', currentPath);

  return (
    <Helmet>
      {/* Title */}
      <title>{finalTitle}</title>

      {/* Robots directive */}
      {finalNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Canonical - always unique per page/language combo */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang tags */}
      <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />
      {hreflangLinks.map((link) => (
        <link key={link.lang} rel="alternate" hrefLang={link.lang} href={link.url} />
      ))}

      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      {finalDescription && <meta property="og:description" content={finalDescription} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={currentLang === 'sv' ? 'sv_SE' : currentLang === 'da' ? 'da_DK' : currentLang === 'no' ? 'nb_NO' : currentLang === 'fi' ? 'fi_FI' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:title" content={finalTitle} />
      {finalDescription && <meta name="twitter:description" content={finalDescription} />}

      {/* Description */}
      {finalDescription && <meta name="description" content={finalDescription} />}
    </Helmet>
  );
};
